import { useState } from "react";
import ContactCard from "../../components/ContactCard";
import { Mail } from "lucide-react";

export default function ContactSection () {

  const socialLinks = [
    {
      platform: "instagram",
      title: "Instagram",
      description: "Follow our updates",
      url: "https://www.instagram.com/ecom_tamil/",
      target: "_blank",
      rel: "noopener noreferrer",
      borderColor: "border-pink-500",
      hoverBg: "hover:bg-pink-50"
    },
    {
      platform: "facebook",
      title: "Facebook",
      description: "Join our community",
      url: "https://www.facebook.com/profile.php?id=61579631565605",
      target: "_blank",
      rel: "noopener noreferrer",
      borderColor: "border-blue-600",
      hoverBg: "hover:bg-blue-50"
    },
    {
      platform: "whatsapp",
      title: "WhatsApp",
      description: "Chat with us",
      url: "https://wa.me/919360326064?text=Hi!%20I%27m%20interested%20in%20learning%20more%20about%20your%20E-Commerce%20masterclass.",
      target: "_blank",
      rel: "noopener noreferrer",
      borderColor: "border-green-500",
      hoverBg: "hover:bg-green-50"
    },
    {
      platform: "email",
      title: "Email",
      description: "Send us a message",
      url: "mailto:ecomtamil.official@gmail.com",
      target: "_self",
      rel: null,
      borderColor: "border-red-500",
      hoverBg: "hover:bg-red-50"
    }
  ];

  return (
    <section id="contact" className="py-16 bg-white relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] -mr-48 -mt-48 rounded-full bg-amber-400/10 blur-[120px] animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] -ml-48 -mb-48 rounded-full bg-orange-400/10 blur-[120px] animate-pulse" style={{animationDelay: '1.5s'}}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-amber-300/5 blur-[150px]"></div>
      
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmIiIGZpbGwtb3BhY2l0eT0iMC4wNCI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          {/* Decorative badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200/50 text-xs font-semibold text-amber-600 uppercase tracking-wider mb-6">
            <span className="flex items-center justify-center w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            Connect With Us
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight">
            Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600">Touch</span>
          </h2>
          
          <div className="w-24 h-1.5 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full mx-auto mb-6"></div>
          
          <p className="max-w-2xl mx-auto text-gray-600 text-lg leading-relaxed">
            Stay connected with us through our social platforms. We'd love to hear from you.
          </p>
        </div>

        {/* Contact Cards Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-6 lg:gap-8">
          {socialLinks?.map((link, index) => (
            <ContactCard
              key={index}
              index={index}
              platform={link?.platform}
              title={link?.title}
              description={link?.description}
              url={link?.url}
              target={link?.target}
              rel={link?.rel}
              borderColor={link?.borderColor}
              hoverBg={link?.hoverBg}
              onClick={link?.onClick}
            />
          ))}
        </div>

        {/* Contact info card */}
        <div className="mt-8 sm:mt-10 max-w-4xl mx-auto bg-gradient-to-r from-amber-50/40 to-orange-50/40 border border-amber-100/60 rounded-2xl sm:rounded-3xl p-5 sm:p-6 shadow-[0_4px_20px_-4px_rgba(251,191,36,0.1)] relative z-20 text-center">
          <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-5">Reach Out to Us</h3>
          
          <div className="flex flex-col lg:flex-row items-center justify-center gap-5 lg:gap-7 flex-wrap">
            {/* Email */}
            <a 
              href="mailto:ecomtamil.official@gmail.com"
              className="flex items-center gap-3 group hover:opacity-90 transition-opacity"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-xs border border-orange-100/80 text-orange-500 shrink-0 group-hover:scale-105 transition-transform">
                <Mail size={18} strokeWidth={2} />
              </div>
              <span className="text-slate-700 text-sm sm:text-base font-medium group-hover:text-orange-600 transition-colors">
                ecomtamil.official@gmail.com
              </span>
            </a>

            {/* Vertical Divider */}
            <div className="hidden lg:block w-px h-8 bg-amber-200/60"></div>
            
            {/* WhatsApp */}
            <a
              href="https://wa.me/918939326064?text=Hi!%20I%27m%20interested%20in%20learning%20more%20about%20your%20E-Commerce%20masterclass."
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex items-center justify-center gap-2.5 bg-white border-2 border-[#25D366] hover:bg-[#25D366] hover:text-white text-black font-bold py-2.5 px-5 rounded-full transform hover:-translate-y-0.5 transition-all duration-300 w-fit cursor-pointer shadow-xs hover:shadow"
            >
              <svg className="w-4.5 h-4.5 text-[#25D366] group-hover:text-white transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              <span className="tracking-wide text-sm">Chat on WhatsApp</span>
            </a>

            {/* Vertical Divider */}
            <div className="hidden lg:block w-px h-8 bg-amber-200/60"></div>

            {/* Our Community */}
            <div className="flex flex-col items-center gap-2">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Our Community</span>
              <div className="flex items-center gap-2">
                {/* WhatsApp */}
                <a href="https://wa.me/918939326064?text=Hi!%20I%27m%20interested%20in%20learning%20more%20about%20your%20E-Commerce%20masterclass." target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-slate-700 hover:text-orange-500 hover:border-orange-100 transition-all duration-200 cursor-pointer shadow-xs hover:shadow">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </a>

                {/* Instagram */}
                <a href="https://www.instagram.com/ecom_tamil/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-slate-700 hover:text-orange-500 hover:border-orange-100 transition-all duration-200 shadow-xs hover:shadow">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.266.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>

                {/* Facebook */}
                <a href="https://www.facebook.com/profile.php?id=61579631565605" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-slate-700 hover:text-orange-500 hover:border-orange-100 transition-all duration-200 shadow-xs hover:shadow">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
                  </svg>
                </a>

                {/* YouTube */}
                <a href="https://www.youtube.com/@ecom_tamil" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-slate-700 hover:text-orange-500 hover:border-orange-100 transition-all duration-200 shadow-xs hover:shadow">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom decorative element */}
        <div className="mt-8 sm:mt-10 flex justify-center items-center gap-4">
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-300"></div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-400"></div>
            <div className="w-2 h-2 rounded-full bg-orange-400"></div>
            <div className="w-2 h-2 rounded-full bg-amber-400"></div>
          </div>
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-300"></div>
        </div>
      </div>

    </section>
  );
};